import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Phone, Mail, Globe, MapPin } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", country: "", company: "", service: "", message: "" };

export const Contact = () => {
  const { t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success(c.success);
      setForm(empty);
    } catch (err) {
      toast.error(c.error);
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-transparent border-b border-white/15 py-4 text-white placeholder:text-zinc-600 focus:border-gold outline-none transition-colors duration-300";

  return (
    <section id="contact" className="relative py-24 md:py-32 bg-[#0A0A0B] border-t border-white/10" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-16">
        <div className="md:col-span-5">
          <Reveal>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-gold" />
              <span className="flo-label">{c.label}</span>
            </div>
            <h2 className="font-serif-display text-white text-4xl sm:text-5xl md:text-6xl tracking-tighter leading-[0.98] mb-8">
              {c.title}
            </h2>
            <p className="text-zinc-400 leading-relaxed max-w-sm text-lg">{c.body}</p>

            <div className="mt-10 space-y-6 border-t border-white/10 pt-8">
              <a
                href={`tel:${c.phoneHref}`}
                data-testid="contact-phone"
                className="group flex items-start gap-4"
              >
                <Phone size={20} className="text-gold mt-1 shrink-0" strokeWidth={1.4} />
                <span>
                  <span className="flo-label block mb-1">{c.phoneLabel}</span>
                  <span className="font-serif-display text-white text-xl md:text-2xl group-hover:text-gold transition-colors duration-300">
                    {c.phone}
                  </span>
                </span>
              </a>
              <a
                href={`mailto:${c.emailValue}`}
                data-testid="contact-email"
                className="group flex items-start gap-4"
              >
                <Mail size={20} className="text-gold mt-1 shrink-0" strokeWidth={1.4} />
                <span>
                  <span className="flo-label block mb-1">{c.emailLabel}</span>
                  <span className="font-serif-display text-white text-xl md:text-2xl group-hover:text-gold transition-colors duration-300 break-all">
                    {c.emailValue}
                  </span>
                </span>
              </a>
              <div className="flex items-start gap-4" data-testid="contact-address">
                <MapPin size={20} className="text-gold mt-1 shrink-0" strokeWidth={1.4} />
                <span>
                  <span className="flo-label block mb-1">{c.addressLabel}</span>
                  <span className="text-zinc-300 text-sm leading-relaxed max-w-xs block">{c.address}</span>
                </span>
              </div>
              <div className="flex items-start gap-4" data-testid="contact-intl-note">
                <Globe size={20} className="text-gold mt-1 shrink-0" strokeWidth={1.4} />
                <p className="text-zinc-400 text-sm leading-relaxed max-w-xs">{c.intlNote}</p>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="md:col-span-7">
          <Reveal delay={0.15}>
            <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <input required value={form.name} onChange={set("name")} placeholder={c.name} className={field} data-testid="input-name" />
                <input required type="email" value={form.email} onChange={set("email")} placeholder={c.email} className={field} data-testid="input-email" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <input required value={form.country} onChange={set("country")} placeholder={c.country} className={field} data-testid="input-country" />
                <input value={form.company} onChange={set("company")} placeholder={c.company} className={field} data-testid="input-company" />
              </div>
              <select required value={form.service} onChange={set("service")} className={`${field} appearance-none cursor-pointer`} data-testid="input-service">
                <option value="" disabled className="bg-[#18181B]">{c.selectService}</option>
                {c.services.map((s) => (
                  <option key={s} value={s} className="bg-[#18181B]">{s}</option>
                ))}
              </select>
              <textarea required rows={4} value={form.message} onChange={set("message")} placeholder={c.message} className={`${field} resize-none`} data-testid="input-message" />
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="group inline-flex items-center gap-2 bg-gold text-[#0A0A0B] font-medium rounded-full pl-7 pr-6 py-4 hover:bg-[#d8b877] transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? c.sending : c.submit}
                <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
